import { ROUTES } from '@/routes/paths';
import type { BestPriceTone, MyBestOffer } from '@/types/bestPrice';

// Тир лучшей цены по выгоде относительно моей цены (со знаком; >0 — выигрыш). Единый источник правила
// «выгодно vs рынок» для всех привязанных мест (дашборд, «Продам», грид «Купить»). undefined → 'plain'.
// Ровно моя цена (=0) → 'market' (мягкая кнопка): это не выигрыш, согласовано с нейтральной дельтой
// «ровно ваша цена» в AdvantageHint (иначе залитая кнопка «выигрыш» спорила бы с нейтральной подписью).
export function bestPriceTone(advantage: number | null | undefined): BestPriceTone {
  // null/undefined (нет привязки) или битое число (NaN/Infinity) → нейтральный вид без оценки.
  if (advantage == null || !Number.isFinite(advantage)) {
    return 'plain';
  }
  // Порог согласован с AdvantageHint (Math.round === 0 → «Ровно моя цена»): подрублёвая разница —
  // это ничья (market), иначе залитая кнопка «выигрыш» спорила бы с нейтральной подписью.
  return Math.round(advantage) === 0 ? 'market' : advantage > 0 ? 'gain' : 'market';
}

// Свести обе стороны для виджета дашборда «Лучшие предложения» и оставить только ВЫГОДНЫЕ встречные
// (advantage >= 0): для продажи — покупают по моей цене ИЛИ ВЫШЕ, для покупки — продают по моей цене ИЛИ
// НИЖЕ (одна проверка ловит обе стороны: бэкенд нормализует advantage так, что >=0 = в мою пользу).
// Невыгодные (advantage < 0) и без оценки (нет advantage) на дашборде не показываем. Сортировка — по
// выгоде убыванием (самые выгодные сверху, обе стороны вперемешку). Карточки офферов фильтр НЕ трогает.
export function combineBeneficialOffers(
  sell: readonly MyBestOffer[],
  buy: readonly MyBestOffer[],
): MyBestOffer[] {
  return [...sell, ...buy]
    .filter((offer) => Number.isFinite(offer.advantage) && (offer.advantage as number) >= 0)
    .sort((a, b) => {
      // Все advantage здесь конечны и >=0; сравнение, а не вычитание (на всякий — без NaN).
      const ka = a.advantage as number;
      const kb = b.advantage as number;
      return ka === kb ? 0 : kb > ka ? 1 : -1;
    });
}

// Ссылка из «Лучших предложений» (виджет дашборда И карточка оффера) на страницу встречного предложения
// с действием: BUY — деталь оффера продавца с «Купить» (/app/buy/offer), SELL — деталь встречной закупки
// покупателя с «Продать» (/app/sell/counter). Параметры совпадают с контрактом этих страниц.
// goods — РОДИТЕЛЬСКИЙ тип культуры (узлы дерева/группы — «Пшеница»), фолбэк на конкретный класс.
export function bestOfferLink(offer: MyBestOffer): string {
  const groupGoods = offer.goodsParentType ?? offer.goodsType;
  // Без встречной позиции вести на страницу действия некуда → деталь группы (управление позицией).
  if (offer.counterpartyOrderId == null || offer.counterpartyOrderVersion == null) {
    return groupLink(offer, groupGoods);
  }
  const params = new URLSearchParams();
  if (groupGoods) {
    params.set('goods', groupGoods);
  }
  // Передаём выгоду и МОЮ цену (авторитетные значения с бэкенда) на страницу встречного предложения —
  // на самой странице их неоткуда взять. Из поиска (привязки нет) оба отсутствуют. Мою цену берём
  // напрямую (offer.myPrice), а не реконструируем из лучшей цены и выгоды — единый источник правды.
  if (offer.advantage != null) {
    params.set('advantage', String(offer.advantage));
  }
  if (offer.myPrice != null) {
    params.set('myPrice', String(offer.myPrice));
  }

  if (offer.side === 'BUY') {
    params.set('saleOrderId', String(offer.counterpartyOrderId));
    params.set('saleOrderVersion', String(offer.counterpartyOrderVersion));
    params.set('fromPurchaseOrderId', String(offer.myOrderId));
    if (offer.address) {
      params.set('toAddress', offer.address);
    }
    if (offer.addressTitle) {
      params.set('toAddressTitle', offer.addressTitle);
    }
    return `${ROUTES.APP_BUY_OFFER}?${params.toString()}`;
  }

  // SELL: продавец отвечает на встречную закупку покупателя.
  params.set('purchaseOrderId', String(offer.counterpartyOrderId));
  params.set('purchaseOrderVersion', String(offer.counterpartyOrderVersion));
  params.set('fromSaleOrderId', String(offer.myOrderId));
  if (offer.address) {
    params.set('fromAddress', offer.address);
  }
  if (offer.addressTitle) {
    params.set('fromAddressTitle', offer.addressTitle);
  }
  if (offer.prepaymentType) {
    params.set('prepayment', offer.prepaymentType);
  }
  return `${ROUTES.APP_SELL_COUNTER}?${params.toString()}`;
}

// Фолбэк-ссылка (когда встречной позиции нет): BUY — деталь прайса (страница группы «Купить» осталась);
// SELL — список «Продать» с предвыбором базиса+культуры (промежуточная страница одного базиса убрана).
function groupLink(offer: MyBestOffer, groupGoods?: string): string {
  const params = new URLSearchParams();
  if (offer.address) {
    params.set('address', offer.address);
  }
  if (offer.addressTitle) {
    params.set('addressTitle', offer.addressTitle);
  }
  if (groupGoods) {
    params.set('goods', groupGoods);
  }
  // Деталь прайс-листа открывается по базису и культуре (года у закупки нет, решение 2026-11-04); без них —
  // список «Купить» с тем фильтром, что есть, а не экран «не найдено».
  const fullGroup = offer.side === 'BUY' && params.has('address') && params.has('addressTitle') && params.has('goods');
  const base = offer.side === 'BUY' ? (fullGroup ? ROUTES.APP_BUY_GROUP : ROUTES.APP_BUY) : ROUTES.APP_SELL;
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}
